"use client";
import { cache, useCallback, useEffect, useState } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { CloseCircleFilled, SearchOutlined } from "@ant-design/icons";
import { AutoComplete, type AutoCompleteProps, Input } from "antd";

import { OngoingSession } from "@/app/abertura-de-vagas/views/mounted-molecules/ongoing-session-modal";
import { Button } from "@/components/atoms/button/button";
import useClientBackend from "@/hooks/useBackend/useClientBackend";
import { useDebounce } from "@/hooks/useDebounce";

import { useSearchContext } from "./search.context";

const getData = cache(async (value: string) => {
  const { getPanoramaCargoByName } = useClientBackend();
  const response = await getPanoramaCargoByName(value);
  return response;
});

const optionItem = (value: string, label: string, _id: string) => ({
  value: label,
  label: <div className="size-full rounded-l-3xl rounded-r-none">{label}</div>,
  key: _id,
  _id,
});

type Props = {
  readonly defaultValue?: string;
};

export default function AutoCompleteBar({ defaultValue }: Props) {
  const [searchValue, setSearchValue] = useState<string>(defaultValue ?? "");
  // const [cargoId, setCargoId] = useState<string>("");
  const [options, setOptions] = useState<AutoCompleteProps["options"]>([]);
  const { setSearch, search } = useSearchContext();
  const router = useRouter();
  const debouncedValue = useDebounce(searchValue, 300);

  const searchParams = useSearchParams();
  const embedded = searchParams.get("embedded");
  const { setValues } = OngoingSession.useContext();

  const handleSearch = useCallback(
    async (value: string) => {
      const { data } = await getData(value);

      if (data.data?.isRelated === false) {
        const dataLabels = data.data?.cargos.map((option: any) =>
          optionItem(
            option.alias ?? option.name,
            option.alias ?? option.name,
            option._id,
          ),
        );
        setOptions(dataLabels);
      } else {
        setOptions([optionItem(value, `Abrir vaga "${value}"`, value)]);
      }
      setSearch({ ...data.data, value });
    },
    [setSearch],
  );

  useEffect(() => {
    if (debouncedValue && debouncedValue.length > 2) {
      handleSearch(debouncedValue).catch(console.error);
    }
    // } else setSearch(undefined);
  }, [debouncedValue, handleSearch, setSearch]);

  const onChange = (search: string) => {
    if (!search.length) {
      return setOptions([]);
    }
    setSearchValue(search.trim());
  };

  const onSelect = (data: string, option: any) => {
    setSearch({ ...search!, isRelated: false });
    if (option._id) {
      setValues({ step: 0, cargoId: option._id, name: option.value });
      if (embedded)
        window.open(`/abertura-de-vagas/${option._id}?embedded=true`, "_blank");
      else router.push(`/abertura-de-vagas/${option._id}`);
    }
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (search?.value && !search?.isRelated && search?.cargos?.length === 0) {
        setValues({ step: 0, cargoId: search.value, name: search.value });
        if (embedded)
          window.open(
            `/abertura-de-vagas/${search.value}?embedded=true`,
            "_blank",
          );
        else router.push(`/abertura-de-vagas/${search.value}`);
      }
      handleSearch(debouncedValue).catch(console.error);
    },
    [search, router, handleSearch, debouncedValue, setValues, embedded],
  );

  return (
    <form className="flex w-full justify-center" onSubmit={handleSubmit}>
      <AutoComplete
        autoFocus
        className="size-full rounded-l-3xl rounded-r-none border-2 border-r-0 border-solid border-raven-100 bg-dr-white-100 text-base md:max-w-[748px] lg:max-w-[849px]"
        options={options}
        onChange={onChange}
        onSelect={onSelect}
        defaultActiveFirstOption
        defaultValue={defaultValue}
        // notFoundContent={
        //   searchValue?.length > 3 ? "Nenhum resultado encontrado" : undefined
        // }
      >
        <Input
          className="size-full rounded-l-3xl rounded-r-none border-none"
          placeholder="insira a vaga ou profissão"
          allowClear={{
            clearIcon: (
              <span
                className="flex items-center justify-center p-0.5 text-xs font-semibold"
                onClick={() => setSearch(undefined)}
              >
                <CloseCircleFilled className="text-base text-aubergine-perl-500" />
              </span>
            ),
          }}
          onChange={(e) => {
            if (e.currentTarget.value.length === 0) setSearch(undefined);
          }}
        />
      </AutoComplete>
      <Button
        className="h-full rounded-l-3xl"
        color="primary"
        onClick={(e) => {
          e.preventDefault();
          onSelect(searchValue, options![0]);
        }}
      >
        <SearchOutlined />
      </Button>
    </form>
  );
}
